import { useContext } from 'react';
import { PROJECT_IDS_LIST, PROJECT_ID } from '../config.js';
import { CheckoutContext } from '../services/checkout-context.js';

const useCheckoutContext = (id) => {
    const state = useContext(CheckoutContext);
    const { onError, pageType, isLoading, isLastContainerActive, isDeliveryAndInvoiceCollapsed, isPaymentCollapsed, setPrevArticle, setArticleQuantity, setFreeSampleArticle, setSubscriptionInterval, setIsSubscriptionValid, removeSubscription, setAddresses, setSavingsPlanArticle, setIsEditStepTriggered, setIsDeliveryAndInvoiceCollapsed, setIsPaymentCollapsed, paymentFrontendErrors, addPaymentFrontendError, setDeliveryService, setIsDeliveryServicesProcessing, updatePreferredPayment, deleteSavedCard, deleteSavedBancontactCard, checkoutState, appConfig, paypalOnApprove, paypalOnCancel, applePayOnSubmit, applePayOnFatalError, braintreeOnSubmit, braintreeOnError, } = state;
    if (!id) {
        onError?.(`No ProjectId provided in useCheckoutContext. You need to provide one of these ids: ${PROJECT_IDS_LIST.join(', ')}`);
        return {};
    }
    const getCheckoutSavingsPlanState = () => {
        const checkoutSavingsPlanState = {
            selectedDeliveryCountryIsoCode: checkoutState?.cart?.selectedDeliveryCountryIsoCode ||
                checkoutState?.cart?.deliveryDetails?.selectedDeliveryCountryIsoCode,
            keycloakToken: checkoutState?.stateAttributes?.keycloakData?.keycloakToken,
            isRebrandStyle: true,
            cartArticles: checkoutState?.cart?.articles.map((article) => {
                return {
                    id: article.id,
                    type: article.type,
                    price: article.price,
                    bestPriceArticle: article.bestPriceArticle,
                    quantity: article.quantity,
                    subtotal: article.subtotal,
                };
            }),
            setSavingsPlanArticle,
        };
        return {
            appConfig,
            checkoutSavingsPlanState,
        };
    };
    const getCheckoutSubscriptionState = () => {
        const cart = checkoutState?.cart || {};
        const articles = cart.articles.map(({ id, type, quantity, subscriptionApplied, subscriptionDetails, lineItemDiscounts }) => ({
            id,
            type,
            quantity,
            subscriptionApplied: subscriptionApplied || lineItemDiscounts?.autoshipmentDiscount?.applied,
            subscriptionDetails: subscriptionDetails || lineItemDiscounts?.autoshipmentDiscount,
        }));
        const checkoutSubscriptionState = {
            customerType: checkoutState?.customer?.customerType || '',
            articles,
            subscriptionInterval: cart.cartSubscription?.interval || cart.cartAutoshipment?.interval,
            selectedDeliveryCountry: cart.selectedDeliveryCountry || cart.deliveryDetails?.selectedDeliveryCountry,
            selectedDeliveryCountryIsoCode: cart.selectedDeliveryCountryIsoCode || cart.deliveryDetails?.selectedDeliveryCountryIsoCode,
            zipCode: cart.deliveryZipCode || cart.deliveryDetails?.deliveryZipCode,
            validSubscription: cart.cartSubscription?.valid || cart.cartAutoshipment?.valid,
            currency: cart.currency,
            sid: cart.sid,
            offerContent: checkoutState?.autoshipment?.offerContent,
            setSubscriptionInterval,
            removeSubscription,
            setIsSubscriptionValid,
        };
        return {
            appConfig,
            checkoutSubscriptionState,
        };
    };
    const getCustomerAddressesState = () => {
        const selectedDeliveryService = checkoutState?.delivery?.selectedDeliveryService;
        const customer = checkoutState?.customer;
        const customerAddressesState = {
            isAddressesLoading: isLoading,
            customerType: customer?.customerType || '',
            deliveryAddress: customer?.deliveryAddress,
            invoiceAddress: customer?.invoiceAddress,
            invoiceSameAsDelivery: customer?.invoiceSameAsDelivery,
            pudoInfo: selectedDeliveryService?.pudoInfo,
            addressMessages: checkoutState?.stateMessages?.addressMessages,
            isRebrandStyle: true,
            isLastContainerActive,
            isDeliveryAndInvoiceCollapsed,
            setAddresses,
            setIsDeliveryAndInvoiceCollapsed,
            setIsEditStepTriggered,
        };
        return {
            appConfig,
            customerAddressesState,
        };
    };
    const getPaymentState = () => {
        const payment = checkoutState?.payment;
        const paymentMessages = checkoutState?.stateMessages?.paymentMessages;
        const cart = checkoutState?.cart;
        const customer = checkoutState?.customer;
        const paymentState = {
            isPaymentLoading: isLoading,
            paymentMethods: payment?.paymentMethods,
            preferredPayment: payment?.preferredPayment,
            savedCards: payment?.savedCards,
            sepaExpressButton: payment?.sepaExpressButton,
            autoshipmentReadyForOrder: checkoutState?.autoshipment?.readyForOrder,
            isRebrandStyle: true,
            noPaymentMethodAvailableForMarketPlace: payment?.noPaymentMethodAvailableForMarketPlace,
            customerHasCreditCardAutoshipment: payment?.customerHasCreditCardAutoshipment,
            ravelinRecommendation: payment?.ravelinRecommendation,
            paymentMessages: [
                ...(paymentMessages ? paymentMessages : []),
                ...(paymentFrontendErrors ? paymentFrontendErrors : []),
            ],
            isPlaceOrderButtonEnabled: checkoutState?.placeOrderConfig?.placeOrderButtonEnabled,
            customerId: customer.customerId,
            customerType: customer?.customerType || '',
            currency: cart.currency,
            grandTotal: cart.summary?.grandTotal,
            sid: cart.sid,
            isPaymentCollapsed,
            addPaymentFrontendError,
            setIsPaymentCollapsed,
            updatePreferredPayment,
            deleteSavedCard,
            deleteSavedBancontactCard,
        };
        if (payment?.paypalButton) {
            paymentState.paypalButton = {
                ...payment.paypalButton,
                onApprove: paypalOnApprove,
                onCancel: paypalOnCancel,
            };
        }
        if (payment?.applePayButton) {
            paymentState.applePayButton = {
                ...payment.applePayButton,
                onSubmit: applePayOnSubmit,
                onFatalError: applePayOnFatalError,
            };
        }
        if (payment?.braintreeButton) {
            paymentState.braintreeButton = {
                ...payment.braintreeButton,
                onSubmit: braintreeOnSubmit,
                onError: braintreeOnError,
            };
        }
        return {
            appConfig,
            paymentState,
        };
    };
    const getCheckoutDeliveryServicesState = () => {
        const cart = checkoutState?.cart;
        // const deliveryAddress = checkoutState?.customer?.deliveryAddress;
        const selectedDeliveryService = checkoutState?.delivery?.selectedDeliveryService;
        // uncomment later & pass in below checkoutDeliveryServicesState object: These are the customDeliveryAddress object and paymentMethod to be passed to the delivery service component
        // deliveryAddress: {
        //   country: deliveryAddress?.country,
        //   region: deliveryAddress?.countryShortName,
        //   postCode: deliveryAddress?.postalCode,
        //   street: deliveryAddress?.street,
        //   phoneNumber: deliveryAddress?.phoneNumber,
        // },
        // paymentMethod: checkoutState.payment.preferredPayment?.paymentMethod,
        const checkoutDeliveryServicesState = {
            cart: {
                cartId: cart?.cartId,
                articles: cart?.articles,
                discounts: cart?.discounts,
                fees: cart?.fees,
                grandTotal: cart?.summary?.grandTotal,
            },
            deliveryAddress: undefined,
            paymentMethod: '',
            subscriptionReadyForOrder: checkoutState?.autoshipment?.readyForOrder,
            deliveryService: selectedDeliveryService?.deliveryService,
            pudoInfo: selectedDeliveryService?.pudoInfo,
            orderDsInfo: selectedDeliveryService?.orderDsInfo,
            customer: {
                customerId: checkoutState?.customer.customerId,
                customerType: checkoutState?.customer.customerType,
                email: checkoutState?.customer.email,
            },
            setIsDeliveryServicesProcessing,
            setDeliveryService,
        };
        return {
            checkoutDeliveryServicesState,
            appConfig,
        };
    };
    switch (id) {
        case PROJECT_ID.CHECKOUT_FRONTEND:
            return state;
        case PROJECT_ID.RECOMMENDATION:
            return {
                pageType,
                isLoading,
                setPrevArticle,
                setArticleQuantity,
                checkoutState,
                appConfig,
            };
        case PROJECT_ID.SAMPLES:
            return {
                appConfig,
                checkoutState,
                setFreeSampleArticle,
            };
        case PROJECT_ID.AUTOSHIPMENT:
            return getCheckoutSubscriptionState();
        case PROJECT_ID.DELIVERY_AND_INVOICE_ADDRESSES:
            return getCustomerAddressesState();
        case PROJECT_ID.PAYMENT_FRONTEND:
            return getPaymentState();
        case PROJECT_ID.DELIVERY_SERVICES:
            return getCheckoutDeliveryServicesState();
        case PROJECT_ID.SAVINGS_PLAN:
            return getCheckoutSavingsPlanState();
        default:
            onError?.(`Wrong ProjectId (${id}) provided in useCheckoutContext. You need to provide one of these ids: ${PROJECT_IDS_LIST.join(', ')}`);
            return {};
    }
};

export { useCheckoutContext as default };
//# sourceMappingURL=use-checkout-context.js.map
